import { EChartOption } from 'echarts'
import { parsePhoneNumberFromString } from 'libphonenumber-js/min'
import { FC, UIEvent, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { ReactComponent as ChevronIcon } from 'src/assets/svg/chevron.svg'
import { ReactComponent as FacebookIcon } from 'src/assets/svg/facebook.svg'
import { ReactComponent as InstagramIcon } from 'src/assets/svg/instagram.svg'
import { ReactComponent as PersonIcon } from 'src/assets/svg/ic-round-person-2.svg'
import useOutsideClick from 'src/hooks/useOutsideClick'
import { platformDisplayLabel } from 'src/constants/platformLabels'
import { ReactComponent as LinkIcon } from 'src/assets/svg/link.svg'
import { ReactComponent as WhatsappIcon } from 'src/assets/svg/whatsapp.svg'
import { ReactComponent as BookingIcon } from 'src/assets/svg/booking-brand.svg'
import { ReactComponent as SentimentChangeIcon } from 'src/assets/svg/change-sentiment.svg'
import { ReactComponent as CheckboxWhiteIcon } from 'src/assets/svg/checkbox-white.svg'
import { ReactComponent as CheckedIcon } from 'src/assets/svg/checked.svg'
import { ReactComponent as DeselectIcon } from 'src/assets/svg/deselect.svg'
import { ReactComponent as ExpediaIcon } from 'src/assets/svg/expedia-brand.svg'
import { EChart } from 'src/charts/EChart'
import { Skeleton } from 'src/components/Skeleton'
import en from 'src/constants/en'
import {
  InboxSentiment,
  useChangeInboxSentimentMutation,
} from 'src/reactQuery/useChangeInboxSentimentMutation'
import {
  SentimentTab,
  useGetInboxPreviewConversationsQuery,
} from 'src/reactQuery/useGetInboxPreviewConversationsQuery'
import { useGetInboxAvailablePlatformsQuery } from 'src/reactQuery/useGetInboxAvailablePlatformsQuery'
import { useGetInboxConversationMessagesQuery } from 'src/reactQuery/useGetInboxConversationMessagesQuery'
import { useGetInboxPreviewSummaryQuery } from 'src/reactQuery/useGetInboxPreviewSummaryQuery'
import {
  ConversionStatusAction,
  useSetConversionStatusMutation,
} from 'src/reactQuery/useSetConversionStatusMutation'
import {
  ConversationPreview,
  FacetEntry,
  InboxPreviewConversationMessagesResponse,
} from 'src/services/__generated__/api'

type InboxPreviewConversationMessage =
  InboxPreviewConversationMessagesResponse['messages'][number]
type TopicFacetEntry = FacetEntry & { children?: FacetEntry[] }
import { resolveExternalUrl } from 'src/utils/resolveExternalUrl'
import {
  buildHighlightRegex,
  HighlightedText,
  MessageText,
} from './MessageText'
import { AudienceSourceSummary } from './AudienceSourceSummary'
import {
  CONVERSION_ACTIONS,
  CONVERSION_FILTER_OPTIONS,
  ConversionActionIcon,
  ConversionBadge,
  ConversionDisplay,
  ConversionFilterValue,
  ConversionAvatarMarker,
  ConversionOutcomeBar,
  ConversionStatusMenu,
  isConversionDisplayOn,
  MarkConversionGlyph,
} from './ConversionStatus'
import conversionClasses from './ConversionStatus/conversionStatus.module.scss'
import { voiceAttachment, VoiceMessage } from './VoiceMessage'
import classes from './inboxPreview.module.scss'

interface InboxPreviewProps {
  fromDate?: string
  pageId: string
  toDate?: string
}

const TABS: { key: SentimentTab; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'positive', label: 'Positive' },
  { key: 'negative', label: 'Negative' },
  { key: 'other', label: 'Neutral' },
]

// Shown in place of the conversation list when the active tab has nothing to
// show. Phrased per tab so an empty Negative tab reads as good news rather than
// as something being broken.
const LIST_EMPTY_COPY: Record<
  SentimentTab,
  { subtitle: string; title: string }
> = {
  all: {
    subtitle: 'Try adjusting your filters to see more conversations.',
    title: 'No conversations to show',
  },
  negative: {
    subtitle: 'Nothing negative here right now — that’s good news.',
    title: 'No negative conversations',
  },
  other: {
    subtitle: 'Every conversation here has a positive or negative tone.',
    title: 'No neutral conversations',
  },
  positive: {
    subtitle: 'No conversations in this view have a positive tone yet.',
    title: 'No positive conversations',
  },
}

const PLATFORM_OPTIONS = [
  { id: 'all', label: 'All Platforms' },
  { id: 'facebook', label: 'Facebook' },
  { id: 'instagram', label: 'Instagram' },
  { id: 'whatsapp', label: 'WhatsApp' },
  { id: 'booking', label: 'Booking' },
  { id: 'expedia', label: 'Expedia' },
] as const

const SENTIMENT_ACTIONS: { key: InboxSentiment; label: string }[] = [
  { key: 'positive', label: 'Positive' },
  { key: 'neutral', label: 'Neutral' },
  { key: 'negative', label: 'Negative' },
]

interface TruncatedTooltipTextProps {
  children: React.ReactNode
  className?: string
  text: string
  tooltipClassName?: string
  wrapClassName?: string
}

const TruncatedTooltipText: FC<TruncatedTooltipTextProps> = ({
  children,
  className,
  text,
  tooltipClassName,
  wrapClassName,
}) => {
  const ref = useRef<HTMLSpanElement>(null)
  const [isTruncated, setIsTruncated] = useState(false)
  const [tipPos, setTipPos] = useState<{ left: number; top: number } | null>(
    null
  )

  useEffect(() => {
    const el = ref.current
    if (!el) return undefined

    const check = () => setIsTruncated(el.scrollWidth > el.clientWidth)
    check()

    const observer = new ResizeObserver(check)
    observer.observe(el)
    return () => observer.disconnect()
  }, [text])

  const handleEnter = () => {
    if (!isTruncated || !ref.current) return
    const rect = ref.current.getBoundingClientRect()
    setTipPos({ left: rect.left, top: rect.bottom + 6 })
  }

  return (
    <span
      className={`${classes.tooltipWrap} ${wrapClassName ?? ''}`}
      onMouseEnter={handleEnter}
      onMouseLeave={() => setTipPos(null)}
    >
      <span className={className} ref={ref}>
        {children}
      </span>
      {tipPos &&
        createPortal(
          <div
            className={tooltipClassName ?? classes.snippetTooltip}
            style={{
              bottom: 'auto',
              left: tipPos.left,
              opacity: 1,
              position: 'fixed',
              top: tipPos.top,
              transform: 'none',
              visibility: 'visible',
            }}
          >
            {text}
          </div>,
          document.body
        )}
    </span>
  )
}

// No hover tooltip here by request: .snippet already ellipsizes, and pasted
// links made the tooltip balloon into a wall of query string that covered the
// conversation being read. The full text is one click away in the panel.
const SnippetCell: FC<{ highlight?: string; text: string }> = ({
  highlight,
  text,
}) => (
  <span className={classes.snippetWrap}>
    <span className={classes.snippet}>
      &quot;
      <HighlightedText highlight={highlight} text={text} />
      &quot;
    </span>
  </span>
)

// With an active search the row shows the message the term was found in
// (not the chat's last message), labelled by who wrote it, so the user can
// see WHY the chat is in the list — the hit is usually an operator reply deep
// in the thread, which the last-message snippet never revealed.
const MatchCell: FC<{
  highlight: string
  match: NonNullable<ConversationPreview['match']>
}> = ({ highlight, match }) => (
  <span className={classes.snippetWrap}>
    <span className={classes.matchLabel}>
      {match.senderType === 'Operator' ? 'Operator' : 'Customer'} wrote
      {match.count > 1 ? ` · ${match.count} matches` : ''}
    </span>
    <span className={classes.snippet}>
      &quot;
      <HighlightedText highlight={highlight} text={match.text} />
      &quot;
    </span>
  </span>
)

const getInitials = (name: string) => {
  const parts = name.trim().split(/\s+/).slice(0, 2)
  return parts.map((p) => p[0]?.toUpperCase() ?? '').join('') || '?'
}

const isoToFlagEmoji = (iso: string) =>
  iso
    .toUpperCase()
    .split('')
    .map((c) => String.fromCodePoint(0x1f1e6 + c.charCodeAt(0) - 65))
    .join('')

const phoneToFlag = (name: string): string | null => {
  const trimmed = name.trim()
  const candidate = trimmed.startsWith('+') ? trimmed : `+${trimmed}`
  const parsed = parsePhoneNumberFromString(candidate)
  return parsed?.country ? isoToFlagEmoji(parsed.country) : null
}

const avatarContent = (platform: string | null, name: string) => {
  if ((platform || '').toLowerCase() === 'whatsapp') {
    const flag = phoneToFlag(name)
    if (flag) return flag
  }
  return getInitials(name)
}

const formatRelative = (iso: string | null) => {
  if (!iso) return ''
  const diffMs = Date.now() - new Date(iso).getTime()
  if (Number.isNaN(diffMs) || diffMs < 0) return ''
  const min = Math.floor(diffMs / 60000)
  if (min < 1) return 'just now'
  if (min < 60) return `${min}m ago`
  const hr = Math.floor(min / 60)
  if (hr < 24) return `${hr}h ago`
  const d = Math.floor(hr / 24)
  if (d < 30) return `${d}d ago`
  const mo = Math.floor(d / 30)
  if (mo < 12) return `${mo}mo ago`
  return `${Math.floor(mo / 12)}y ago`
}

const platformIcon = (platform: string | null) => {
  switch ((platform || '').toLowerCase()) {
    case 'facebook':
      return <FacebookIcon />
    case 'instagram':
      return <InstagramIcon />
    case 'whatsapp':
      return <WhatsappIcon />
    case 'booking':
      return <BookingIcon />
    case 'expedia':
      return <ExpediaIcon />
    default:
      return null
  }
}

// Mirrors a real conversation .row: the 48x48 avatar circle, the two snippet
// lines inside .message, the 64x22 sentiment pill and the 140x14 operator
// label — all dimensions taken from inboxPreview.module.scss so the list
// occupies its final height while loading. Used for both the first load and
// the infinite-scroll page append.
// Audience (New / Returning) and Source (Organic / Paid) filters from the
// design. The chat collector doesn't record customer ids or ad referrals yet
// (HAP-502), so there is nothing to filter on: the control is disabled.
const PendingDataFilter: FC<{
  label: string
  value: string
}> = ({ label, value }) => (
  <div className={classes.operatorDropdown}>
    <button
      className={`${classes.operatorDropdownTrigger} ${classes.pendingTrigger}`}
      disabled
      title={`${label}: processing — available once chats record this data`}
      type="button"
    >
      <span className={classes.operatorDropdownLabel}>{label}</span>
      <span
        className={`${classes.operatorDropdownValue} ${classes.pendingValue}`}
      >
        <span className={classes.filterValueText}>{value}</span>
        <ChevronIcon />
      </span>
    </button>
  </div>
)

export type SourceFilterValue = 'all' | 'organic' | 'paid'

const SOURCE_OPTIONS: { id: SourceFilterValue; label: string }[] = [
  { id: 'all', label: 'Organic / Paid' },
  { id: 'organic', label: 'Organic' },
  { id: 'paid', label: 'Paid' },
]

// Single-choice dropdown styled like the Operator filter. Used for Source
// (Organic / Paid, pages with ad-label attribution — summary.sourceAvailable)
// and, in subtle mode, Conversion (summary.conversionDisplay).
interface SingleChoiceFilterProps<T extends string> {
  // Value shown on the trigger when 'all' is selected, after the "All" accent.
  allValueLabel: string
  label: string
  onChange: (value: T) => void
  options: { count?: number; id: T | 'all'; label: string }[]
  value: T | 'all'
}

const SingleChoiceFilter = <T extends string>({
  allValueLabel,
  label,
  onChange,
  options,
  value,
}: SingleChoiceFilterProps<T>) => {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  useOutsideClick({ onOutsideClick: () => setOpen(false), ref })
  const current = options.find((option) => option.id === value)

  return (
    <div
      className={`${classes.operatorDropdown} ${open ? classes.dropdownOpen : ''}`}
      ref={ref}
    >
      <button
        aria-expanded={open}
        aria-haspopup="listbox"
        className={`${classes.operatorDropdownTrigger} ${
          open ? classes.open : ''
        }`}
        onClick={() => setOpen(!open)}
        title={`${label}: ${value === 'all' ? `All ${allValueLabel}` : current?.label}`}
        type="button"
      >
        <span className={classes.operatorDropdownLabel}>{label}</span>
        <span className={classes.operatorDropdownValue}>
          <span className={classes.filterValueText}>
            {value === 'all' ? (
              <>
                <span className={classes.filterValueAccent}>All</span>{' '}
                {allValueLabel}
              </>
            ) : (
              current?.label
            )}
          </span>
          <ChevronIcon />
        </span>
      </button>

      {open && (
        <div className={classes.operatorDropdownPanel}>
          <div
            aria-label={label}
            className={classes.operatorPanelList}
            role="listbox"
          >
            {options.map((option) => (
              <button
                aria-selected={value === option.id}
                className={classes.operatorPanelOption}
                key={option.id}
                onClick={() => {
                  onChange(option.id as T)
                  setOpen(false)
                }}
                role="option"
                type="button"
              >
                <span
                  className={`${classes.operatorCheckbox} ${
                    value === option.id ? classes.checked : ''
                  }`}
                >
                  {value === option.id && (
                    <svg aria-hidden="true" fill="none" viewBox="0 0 16 16">
                      <path
                        d="M3.5 8.2L6.5 11L12.5 5"
                        stroke="currentColor"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="1.8"
                      />
                    </svg>
                  )}
                </span>
                <span className={classes.operatorPanelName}>
                  {option.id === 'all' ? 'All chats' : option.label}
                </span>
                {typeof option.count === 'number' && (
                  <span className={classes.operatorPanelCount}>
                    · {option.count.toLocaleString()} Chats
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

const SourceFilter: FC<{
  onChange: (value: SourceFilterValue) => void
  value: SourceFilterValue
}> = ({ onChange, value }) => (
  <SingleChoiceFilter<Exclude<SourceFilterValue, 'all'>>
    allValueLabel="Organic / Paid"
    label="Source"
    onChange={onChange}
    options={SOURCE_OPTIONS}
    value={value}
  />
)

const ConversationRowSkeleton: FC = () => (
  <div className={classes.row}>
    <Skeleton circle width={48} />
    <div className={classes.message}>
      <Skeleton
        className={classes.skeletonLine}
        height={10}
        radius={6}
        width="40%"
      />
      <Skeleton className={classes.skeletonLine} height={10} radius={6} />
    </div>
    <Skeleton height={22} radius={999} width={64} />
    <Skeleton height={14} radius={6} width={140} />
  </div>
)

interface DonutProps {
  negative: number
  neutral: number
  percent: number
  positive: number
}

const Donut: FC<DonutProps> = ({ positive, neutral, negative, percent }) => {
  const [hoverPercent, setHoverPercent] = useState<number | null>(null)
  const total = positive + neutral + negative

  useEffect(() => {
    setHoverPercent(null)
  }, [positive, neutral, negative])

  const options = useMemo<EChartOption<EChartOption.SeriesPie>>(
    () => ({
      color: ['#8080FF', '#C7CED1', '#FF6833'],
      series: [
        {
          avoidLabelOverlap: true,
          data: [
            { name: 'Positive', value: positive },
            { name: 'Neutral', value: neutral },
            { name: 'Negative', value: negative },
          ],
          emphasis: {
            // @ts-ignore-next-line
            scale: false,
          },
          itemStyle: {
            borderColor: '#fff',
            borderWidth: 1,
          },
          label: { show: false },
          labelLine: { show: false },
          radius: ['72%', '90%'],
          type: 'pie',
        },
      ],
      tooltip: {
        appendToBody: true,
        confine: false,
        formatter: '{b} : {c}',
        trigger: 'item',
      },
    }),
    [positive, negative, neutral]
  )

  const displayPercent = hoverPercent ?? percent
  const formattedPercent = Number.isInteger(displayPercent)
    ? displayPercent.toString()
    : Number(displayPercent).toFixed(1)

  return (
    <div className={classes.donut}>
      <EChart
        height={100}
        onMouseOut={() => setHoverPercent(null)}
        onMouseOver={(event) => setHoverPercent(event.percent ?? null)}
        options={options}
        width={100}
      />
      <div className={classes.donutCenter}>
        <span className={classes.donutPercent}>{formattedPercent}%</span>
        <span className={classes.donutCount}>{total.toLocaleString()}</span>
      </div>
    </div>
  )
}

const formatMessageTimestamp = (iso: string | null) => {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  return d.toLocaleString(undefined, {
    day: 'numeric',
    hour: 'numeric',
    hour12: true,
    minute: '2-digit',
    month: 'short',
    year: 'numeric',
  })
}

const formatPlatformLabel = (platform: string) => {
  const p = platform.toLowerCase()
  return platformDisplayLabel(p, p.charAt(0).toUpperCase() + p.slice(1))
}

const formatTopicName = (name: string) =>
  name
    .replace(/[_-]+/g, ' ')
    .split(' ')
    .filter(Boolean)
    .map((w) => w[0].toUpperCase() + w.slice(1).toLowerCase())
    .join(' ')

const formatTopicCount = (count: number) =>
  count.toLocaleString('en-US').replace(/,/g, ' ')

const getTopicId = (topic: Pick<FacetEntry, 'id' | 'name'>) =>
  String(topic.id || topic.name || '')

const getTopicName = (topic: Pick<FacetEntry, 'id' | 'name'>) =>
  formatTopicName(String(topic.name || topic.id || 'Untitled topic'))

const getTopicCount = (topic: Pick<FacetEntry, 'count'>) =>
  Number(topic.count || 0)

const OperatorIcon: FC<{ className?: string }> = ({ className }) => (
  <svg
    className={className}
    fill="none"
    height="14"
    viewBox="0 0 14 14"
    width="14"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M7 7.5a2.75 2.75 0 1 0 0-5.5 2.75 2.75 0 0 0 0 5.5Zm0 1.5c-2.21 0-5 1.11-5 3.33V13h10v-.67C12 10.11 9.21 9 7 9Z"
      fill="currentColor"
    />
  </svg>
)

const EmptyInboxIcon: FC<{ className?: string }> = ({ className }) => (
  <svg
    className={className}
    fill="none"
    viewBox="0 0 32 32"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M6.667 9.333c0-1.473 1.194-2.666 2.666-2.666h13.334c1.472 0 2.666 1.193 2.666 2.666v13.334c0 1.472-1.194 2.666-2.666 2.666H9.333c-1.472 0-2.666-1.194-2.666-2.666V9.333Z"
      fill="currentColor"
    />
    <path
      d="m9.333 11.333 6.667 4.8 6.667-4.8"
      stroke="#EDEDED"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2.4"
    />
  </svg>
)

const NoDateRangeDataIcon: FC<{ className?: string }> = ({ className }) => (
  <svg
    className={className}
    fill="none"
    viewBox="0 0 32 32"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      clipRule="evenodd"
      d="M4 9.333C4 7.124 5.791 5.333 8 5.333h16c2.209 0 4 1.791 4 4V18a1.333 1.333 0 1 1-2.667 0V9.333a1.333 1.333 0 0 0-1.333-1.333H8a1.333 1.333 0 0 0-1.333 1.333v13.334A1.333 1.333 0 0 0 8 24h8a1.333 1.333 0 1 1 0 2.667H8c-2.209 0-4-1.791-4-4V9.333Z"
      fill="currentColor"
      fillRule="evenodd"
    />
    <path
      clipRule="evenodd"
      d="M4.781 9.115a1.333 1.333 0 0 1 1.837-.334l9.382 6.286 9.382-6.286a1.333 1.333 0 1 1 1.503 2.2L16 18.218 5.115 10.952a1.333 1.333 0 0 1-.334-1.837Z"
      fill="currentColor"
      fillRule="evenodd"
    />
    <path
      d="M22.667 20a1.333 1.333 0 1 0 0 2.667 1.333 1.333 0 0 0 0-2.667Zm-4 1.333a4 4 0 1 1 8 0 4 4 0 0 1-8 0Z"
      fill="currentColor"
    />
    <path
      d="M22.667 17.333A1.333 1.333 0 0 1 24 18.667V20a1.333 1.333 0 0 1-2.667 0v-1.333a1.333 1.333 0 0 1 1.334-1.334Z"
      fill="currentColor"
    />
  </svg>
)

interface ConversationPanelProps {
  conversation: ConversationPreview
  // Page's conversion display mode (summary.conversionDisplay). Omitted / 'off'
  // hides the status badge.
  conversionDisplay?: ConversionDisplay
  // Active list search: highlighted in every bubble, and the panel opens on
  // the first message containing it instead of the bottom.
  highlight?: string
  // While a "Mark as" request for this chat is in flight.
  isChangingConversion?: boolean
  isLoading: boolean
  messages: InboxPreviewConversationMessage[]
  onClose?: () => void
  // Given: the header badge becomes a "Mark as" menu. Omitted: read-only badge.
  onConversionChange?: (action: ConversionStatusAction) => void
}

export const ConversationPanel: FC<ConversationPanelProps> = ({
  conversation,
  conversionDisplay,
  highlight,
  isChangingConversion = false,
  isLoading,
  messages,
  onClose,
  onConversionChange,
}) => {
  const bodyRef = useRef<HTMLDivElement>(null)
  const topicsPopoverRef = useRef<HTMLDivElement>(null)
  // Hover tooltip listing the extra categories behind the "+N" chip. Portaled
  // to the body so the panel's overflow:hidden can't clip it.
  const [topicsTipPos, setTopicsTipPos] = useState<{
    left: number
    top: number
  } | null>(null)

  const handleTopicsEnter = () => {
    const el = topicsPopoverRef.current
    if (!el) return
    const rect = el.getBoundingClientRect()
    setTopicsTipPos({ left: rect.left, top: rect.bottom + 6 })
  }

  const visibleMessages = useMemo(
    () => messages.filter((m) => m.text && m.text.trim().length > 0),
    [messages]
  )

  const visibleTopics = conversation.topics.slice(0, 1)
  const hiddenTopics = conversation.topics.slice(1)

  const highlightRegex = useMemo(
    () => (highlight ? buildHighlightRegex(highlight) : null),
    [highlight]
  )

  useEffect(() => {
    const body = bodyRef.current
    if (isLoading || !body) return
    const firstHit = highlightRegex
      ? body.querySelector<HTMLElement>('[data-search-hit="true"]')
      : null
    if (firstHit) {
      firstHit.scrollIntoView({ block: 'center' })
    } else {
      body.scrollTop = body.scrollHeight
    }
  }, [isLoading, visibleMessages, highlightRegex])

  return (
    <div className={classes.conversationPanel}>
      <div className={classes.conversationHeader}>
        <div className={classes.conversationTitleRow}>
          <span className={classes.conversationName}>
            {conversation.user.name}
          </span>
          {onClose && (
            <button
              aria-label="Close chat"
              className={classes.conversationClose}
              onClick={onClose}
              type="button"
            >
              ×
            </button>
          )}
        </div>
        <div className={classes.conversationMetaRow}>
          {conversation.platform && (
            <span className={classes.platformChip}>
              {platformIcon(conversation.platform)}
              <span>{formatPlatformLabel(conversation.platform)}</span>
            </span>
          )}
          {visibleTopics.map((t) => (
            <span className={classes.topicChip} key={t.id}>
              <TruncatedTooltipText
                className={classes.topicChipText}
                text={formatTopicName(t.name)}
                tooltipClassName={classes.operatorTooltip}
                wrapClassName={classes.topicChipTextWrap}
              >
                {formatTopicName(t.name)}
              </TruncatedTooltipText>
            </span>
          ))}
          {hiddenTopics.length > 0 && (
            <div
              className={classes.topicMoreWrap}
              onMouseEnter={handleTopicsEnter}
              onMouseLeave={() => setTopicsTipPos(null)}
              ref={topicsPopoverRef}
            >
              <button className={classes.topicMoreChip} type="button">
                +{hiddenTopics.length}
              </button>
              {topicsTipPos &&
                createPortal(
                  <div
                    className={classes.topicPopover}
                    style={{
                      left: topicsTipPos.left,
                      position: 'fixed',
                      top: topicsTipPos.top,
                      transform: 'none',
                      zIndex: 2000,
                    }}
                  >
                    {hiddenTopics.map((t) => (
                      <span className={classes.topicChip} key={t.id}>
                        {formatTopicName(t.name)}
                      </span>
                    ))}
                  </div>,
                  document.body
                )}
            </div>
          )}
          <span
            className={`${classes.conversationSentimentChip} ${
              classes[conversation.sentiment]
            }`}
          >
            {conversation.sentiment}
          </span>
          {isConversionDisplayOn(conversionDisplay) &&
            (onConversionChange ? (
              <ConversionStatusMenu
                disabled={isChangingConversion}
                onSelect={onConversionChange}
                status={conversation.conversion}
                variant={conversionDisplay}
              />
            ) : (
              conversation.conversion && (
                <ConversionBadge
                  placement="header"
                  status={conversation.conversion}
                  variant={conversionDisplay}
                />
              )
            ))}
          {conversation.operator?.name && (
            <span className={classes.operatorMeta}>
              <OperatorIcon className={classes.operatorIcon} />
              Operator:{' '}
              <TruncatedTooltipText
                className={classes.operatorName}
                text={conversation.operator.name}
                tooltipClassName={classes.operatorTooltip}
                wrapClassName={classes.operatorNameWrap}
              >
                {conversation.operator.name}
              </TruncatedTooltipText>
            </span>
          )}
          {(() => {
            const href = resolveExternalUrl(conversation.externalUrl)
            if (!href) return null
            return (
              <a
                className={`${classes.linkIcon} ${classes.conversationLinkIcon}`}
                href={href}
                rel="noopener noreferrer"
                target="_blank"
              >
                <LinkIcon />
              </a>
            )
          })()}
        </div>
      </div>

      <div className={classes.conversationBody} ref={bodyRef}>
        {isLoading &&
          Array.from({ length: 5 }).map((_, i) => {
            const isOperator = i % 2 === 1
            const widths = ['60%', '45%', '72%', '38%', '55%']
            return (
              <div
                className={`${classes.messageBlock} ${
                  isOperator ? classes.fromOperator : classes.fromCustomer
                }`}
                // eslint-disable-next-line react/no-array-index-key
                key={`msg-skeleton-${i}`}
              >
                <div className={classes.messageRow}>
                  {/* .messageAvatar is a 24px circle; the bubbles are 32px
                      tall with the 12px radius they carry in the real
                      layout (see inboxPreview.module.scss). */}
                  {!isOperator && <Skeleton circle width={24} />}
                  <Skeleton height={32} radius={12} width={widths[i]} />
                </div>
              </div>
            )
          })}

        {!isLoading && visibleMessages.length === 0 && (
          <div className={classes.conversationEmpty}>No messages</div>
        )}

        {!isLoading &&
          visibleMessages.map((msg, i) => {
            const isOperator = msg.senderType === 'Operator'
            const prev = i > 0 ? visibleMessages[i - 1] : null
            const prevMs = prev?.createdAt
              ? new Date(prev.createdAt).getTime()
              : null
            const msgMs = msg.createdAt
              ? new Date(msg.createdAt).getTime()
              : null
            const showTime =
              !!msgMs && (prevMs === null || msgMs - prevMs > 15 * 60 * 1000)
            const isHit = !!highlightRegex && highlightRegex.test(msg.text)
            const voice = voiceAttachment(msg.attachments)
            return (
              <div
                className={`${classes.messageBlock} ${
                  isOperator ? classes.fromOperator : classes.fromCustomer
                }`}
                data-search-hit={isHit ? 'true' : undefined}
                key={msg.id}
              >
                {showTime && msg.createdAt && (
                  <div className={classes.messageTime}>
                    {formatMessageTimestamp(msg.createdAt)}
                  </div>
                )}
                <div className={classes.messageRow}>
                  {!isOperator && (
                    <div className={classes.messageAvatar}>
                      {avatarContent(
                        conversation.platform,
                        msg.senderName || conversation.user.name
                      )}
                    </div>
                  )}
                  {voice?.duration ? (
                    <VoiceMessage
                      duration={voice.duration}
                      href={resolveExternalUrl(conversation.externalUrl)}
                      isOperator={isOperator}
                    />
                  ) : (
                    <div
                      className={`${classes.bubble} ${
                        isOperator
                          ? classes.operatorBubble
                          : classes.customerBubble
                      }`}
                    >
                      <MessageText highlight={highlight} text={msg.text} />
                    </div>
                  )}
                </div>
              </div>
            )
          })}
      </div>
    </div>
  )
}

const dominantHeading = (
  dominant: 'positive' | 'neutral' | 'negative' | 'balanced' | undefined
) => {
  switch (dominant) {
    case 'positive':
      return 'Mostly Positive'
    case 'negative':
      return 'Mostly Negative'
    case 'neutral':
      return 'Mostly Neutral'
    case 'balanced':
      return 'Balanced'
    default:
      return ''
  }
}

export const InboxPreview: FC<InboxPreviewProps> = ({
  fromDate,
  pageId,
  toDate,
}) => {
  // Only show platform tabs for platforms that actually have data on this page.
  const { data: availablePlatforms } = useGetInboxAvailablePlatformsQuery({
    fromDate,
    pageId,
    toDate,
  })
  const platformOptions = PLATFORM_OPTIONS.filter(
    (platform) =>
      platform.id === 'all' || (availablePlatforms ?? []).includes(platform.id)
  )

  const [activeTab, setActiveTab] = useState<SentimentTab>('all')
  const [openFilter, setOpenFilter] = useState<
    'platform' | 'topic' | 'operator' | null
  >(null)
  const [selectedPlatforms, setSelectedPlatforms] = useState<string[]>([])
  // null = all topics selected, [] = none selected, [ids] = explicit subset
  // (mirrors the selectedOperators model so "Select all" can toggle off).
  const [selectedTopics, setSelectedTopics] = useState<string[] | null>(null)
  const [topicSearch, setTopicSearch] = useState('')
  // Debounced copy of the search box, used for the CHAT query only (the topic
  // list filters instantly since it's already in memory). Without the delay
  // every keystroke would fire a request.
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [selectedOperators, setSelectedOperators] = useState<string[] | null>(
    null
  )
  const [operatorSearch, setOperatorSearch] = useState('')
  const [selectedSource, setSelectedSource] = useState<SourceFilterValue>('all')
  const [selectedConversion, setSelectedConversion] =
    useState<ConversionFilterValue>('all')
  const [expandedTopicIds, setExpandedTopicIds] = useState<string[]>([])
  const [topicListOpen, setTopicListOpen] = useState(true)
  const operatorDropdownRef = useRef<HTMLDivElement>(null)
  // Reading a chat and marking it are two different things: the open chat
  // (activeConversation) follows a row click, the selection (checkedIds) only
  // follows the row checkbox — as in Review Analysis.
  const [activeConversation, setActiveConversation] =
    useState<ConversationPreview | null>(null)
  const [checkedIds, setCheckedIds] = useState<Set<string>>(new Set())
  const [sentimentMenuOpen, setSentimentMenuOpen] = useState(false)
  const sentimentMenuRef = useRef<HTMLDivElement>(null)
  const [conversionMenuOpen, setConversionMenuOpen] = useState(false)
  const conversionMenuRef = useRef<HTMLDivElement>(null)

  // Close the operator filter dropdown when clicking anywhere outside it.
  useOutsideClick({
    onOutsideClick: () => setOpenFilter(null),
    ref: operatorDropdownRef,
  })

  useOutsideClick({
    onOutsideClick: () => setSentimentMenuOpen(false),
    ref: sentimentMenuRef,
  })

  useOutsideClick({
    onOutsideClick: () => setConversionMenuOpen(false),
    ref: conversionMenuRef,
  })

  const { mutate: changeInboxSentiment, isPending: isChangingSentiment } =
    useChangeInboxSentimentMutation()

  const {
    mutate: setConversionStatus,
    isPending: isChangingConversion,
    variables: conversionVariables,
  } = useSetConversionStatusMutation()

  const { data: messagesData, isLoading: messagesLoading } =
    useGetInboxConversationMessagesQuery({
      conversationId: activeConversation?.id ?? null,
      // The backend caps at 500; the largest real chat is ~340 messages, so
      // a searched-for message is never cut off the end of the panel.
      limit: 500,
    })

  const filterArgs = {
    conversion: selectedConversion === 'all' ? undefined : selectedConversion,
    fromDate,
    operators:
      selectedOperators === null
        ? []
        : selectedOperators.length
          ? selectedOperators
          : ['__none__'],
    pageId,
    platforms: selectedPlatforms,
    source: selectedSource === 'all' ? undefined : selectedSource,
    toDate,
    topics:
      selectedTopics === null
        ? []
        : selectedTopics.length
          ? selectedTopics
          : ['__none__'],
  }

  const {
    data: summary,
    isLoading: summaryLoading,
    isFetching: summaryFetching,
  } = useGetInboxPreviewSummaryQuery(filterArgs)

  const {
    data: conversationsData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading: conversationsLoading,
    isFetching: conversationsFetching,
  } = useGetInboxPreviewConversationsQuery({
    ...filterArgs,
    search: debouncedSearch,
    sentiment: activeTab,
  })

  // Subtle "updating" state for filter/tab changes. Data is kept visible via
  // keepPreviousData, so isLoading is false; isFetching flips true while the new
  // result loads. Excludes initial load (skeletons) and infinite-scroll paging.
  const isUpdating =
    (summaryFetching && !summaryLoading) ||
    (conversationsFetching && !conversationsLoading && !isFetchingNextPage)

  const conversations = useMemo<ConversationPreview[]>(() => {
    if (!conversationsData?.pages) return []
    return conversationsData.pages.flatMap((page) => page?.conversations ?? [])
  }, [conversationsData])
  // When the empty result is the consequence of a filter the user applied, keep
  // the body (and its filter controls) visible so they can undo it. The
  // sentiment tabs and topic/operator pickers live INSIDE the body, so swapping
  // in the empty panel would strand the user with no way back — e.g. after
  // re-classifying every negative conversation as positive, the Negative tab is
  // legitimately empty but must still offer the tabs to switch away from it.
  // The search box lives inside the body too, so a zero-result search must count
  // as a filter — otherwise the panel swallows the very input that caused it and
  // the term can't be cleared. Both the raw and debounced values are checked so
  // the panel can't flash during the debounce window.
  const hasActiveFilter =
    activeTab !== 'all' ||
    topicSearch.trim() !== '' ||
    debouncedSearch !== '' ||
    selectedPlatforms.length > 0 ||
    selectedConversion !== 'all' ||
    (selectedTopics !== null && selectedTopics.length === 0) ||
    (selectedOperators !== null && selectedOperators.length === 0)
  const showEmptyState =
    !conversationsLoading && conversations.length === 0 && !hasActiveFilter
  // A search that matches nothing isn't a sentiment story, so don't reuse the
  // tab copy ("No negative conversations") — name the search as the cause.
  const listEmptyCopy = debouncedSearch
    ? {
        subtitle: `No chats match “${debouncedSearch}”. Try another name or keyword.`,
        title: 'No matching chats',
      }
    : LIST_EMPTY_COPY[activeTab]
  // When a date range is active and there's no data, show the "no data for
  // this period" variant. Without a date range it's a "no data at all" state.
  const hasDateFilter = !!(fromDate || toDate)
  const emptyStateVariant: 'no_data' | 'no_date_range_data' =
    showEmptyState && hasDateFilter ? 'no_date_range_data' : 'no_data'

  useEffect(() => {
    // The open chat is filtered out along with everything else, so close the
    // panel rather than leave it showing a chat the list no longer contains.
    setActiveConversation(null)
  }, [
    activeTab,
    selectedOperators,
    selectedPlatforms,
    selectedTopics,
    fromDate,
    toDate,
  ])

  // Any filter change (tab, platform, topic, operator, search, date) starts the
  // selection over, as in Review Analysis: "Mark as" must never re-label chats
  // that were ticked under a previous filter and are no longer on screen.
  useEffect(() => {
    setCheckedIds(new Set())
    setSentimentMenuOpen(false)
  }, [
    activeTab,
    debouncedSearch,
    selectedOperators,
    selectedPlatforms,
    selectedTopics,
    fromDate,
    toDate,
  ])

  useEffect(() => {
    const timeoutId = setTimeout(
      () => setDebouncedSearch(topicSearch.trim()),
      400
    )
    return () => clearTimeout(timeoutId)
  }, [topicSearch])

  const toggleChecked = (id: string) => {
    setCheckedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const allChecked =
    conversations.length > 0 && checkedIds.size === conversations.length
  const toggleCheckAll = () => {
    setCheckedIds(
      allChecked ? new Set() : new Set(conversations.map((c) => c.id))
    )
  }

  const clearSelection = () => {
    setCheckedIds(new Set())
    setSentimentMenuOpen(false)
  }

  const handleSentimentAction = (sentiment: InboxSentiment) => {
    if (!checkedIds.size || isChangingSentiment) return

    const selectedIds = Array.from(checkedIds)

    if (
      conversations.every(
        (c) => !checkedIds.has(c.id) || c.sentiment === sentiment
      )
    ) {
      setSentimentMenuOpen(false)
      return
    }

    changeInboxSentiment(
      { conversationIds: selectedIds, sentiment },
      {
        onSuccess: () => {
          // Drop the selection instead of re-tagging it: re-classified chats
          // can leave the active sentiment tab, and a count of rows no longer
          // on screen would be meaningless.
          clearSelection()
          // The open chat holds a snapshot of its row, so re-labelling it while
          // it is being read would leave the panel showing the old sentiment.
          setActiveConversation((current) =>
            current && selectedIds.includes(current.id)
              ? { ...current, sentiment }
              : current
          )
        },
      }
    )
  }

  // Shared by the selection bar and the chat header. The open chat holds a
  // snapshot of its row, so it is patched here; the list and the summary
  // refetch through the mutation's invalidation.
  const applyConversionStatus = (
    conversationIds: string[],
    action: ConversionStatusAction,
    onDone?: () => void
  ) => {
    setConversionStatus(
      { conversationIds, pageId, status: action },
      {
        onSuccess: (data) => {
          const written = new Set(data.conversationIds ?? conversationIds)
          setActiveConversation((current) =>
            current && written.has(current.id)
              ? {
                  ...current,
                  conversion: action === 'none' ? undefined : action,
                }
              : current
          )
          onDone?.()
        },
      }
    )
  }

  const handleConversionAction = (action: ConversionStatusAction) => {
    if (!checkedIds.size || isChangingConversion) return
    const selectedIds = Array.from(checkedIds)

    // Nothing to change: every selected chat already has this status.
    if (
      conversations.every(
        (c) => !checkedIds.has(c.id) || (c.conversion ?? 'none') === action
      )
    ) {
      setConversionMenuOpen(false)
      return
    }

    applyConversionStatus(selectedIds, action, () => {
      // Same as sentiment: marked chats can leave an active Conversion
      // filter, so drop the selection rather than keep a stale count.
      setConversionMenuOpen(false)
      clearSelection()
    })
  }

  const handleScroll = (e: UIEvent<HTMLDivElement>) => {
    if (!hasNextPage || isFetchingNextPage) return
    const el = e.currentTarget
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 80) {
      fetchNextPage()
    }
  }

  const donut = summary?.donut
  const facets = summary?.facets

  // Conversion statuses: 'off' hides every control (no data / tenant opted
  // out); 'subtle' = icon-only row markers + dropdown in the filters bar;
  // 'prominent' = labelled pills + segmented filter with counts.
  const conversionDisplay: ConversionDisplay =
    summary?.conversionDisplay ?? 'off'
  const conversionOn = isConversionDisplayOn(conversionDisplay)
  const conversionCounts = summary?.conversionCounts ?? null

  // A filter the UI no longer shows must not keep narrowing the list (e.g.
  // the page switched to one without conversion data).
  useEffect(() => {
    if (!summaryLoading && !conversionOn && selectedConversion !== 'all') {
      setSelectedConversion('all')
    }
  }, [conversionOn, selectedConversion, summaryLoading])
  // Exclude the catch-all "Unassigned" bucket from the operator filter list —
  // it isn't a real operator to filter by.
  const operatorOptions = (facets?.operators ?? []).filter(
    (operator) =>
      operator.id !== 'op-unassigned' &&
      operator.id !== 'Unassigned' &&
      operator.name !== 'Unassigned'
  )
  const normalizedOperatorSearch = operatorSearch.trim().toLowerCase()
  const visibleOperatorOptions = normalizedOperatorSearch
    ? operatorOptions.filter((operator) =>
        (operator.name || '').toLowerCase().includes(normalizedOperatorSearch)
      )
    : operatorOptions
  const selectedOperatorCount =
    selectedOperators === null
      ? operatorOptions.length
      : selectedOperators.length
  const operatorValue =
    selectedOperators === null
      ? 'All Operators Selected'
      : selectedOperatorCount === 0
        ? 'No Operators Selected'
        : `${selectedOperatorCount} Operator${
            selectedOperatorCount === 1 ? '' : 's'
          } Selected`
  const activePlatform =
    selectedPlatforms.length === 1 ? selectedPlatforms[0] : 'all'
  const topicOptions = (facets?.topics ?? []) as TopicFacetEntry[]
  const selectableTopicIds = Array.from(
    new Set(
      [
        ...topicOptions.map(getTopicId),
        ...topicOptions.flatMap((topic) =>
          (topic.children ?? []).map(getTopicId)
        ),
      ].filter(Boolean)
    )
  )
  const allTopicsSelected = selectedTopics === null
  const isTopicSelected = (id: string) =>
    selectedTopics === null || selectedTopics.includes(id)
  const toggleTopic = (id: string) => {
    const current =
      selectedTopics === null ? selectableTopicIds : selectedTopics
    const next = current.includes(id)
      ? current.filter((topicId) => topicId !== id)
      : [...current, id]

    // Collapse back to the "all selected" sentinel only when everything is on.
    setSelectedTopics(
      next.length === selectableTopicIds.length && selectableTopicIds.length > 0
        ? null
        : next
    )
  }
  const toggleAllTopics = () => setSelectedTopics(allTopicsSelected ? [] : null)
  const toggleTopicGroup = (id: string) => {
    setExpandedTopicIds((current) =>
      current.includes(id)
        ? current.filter((topicId) => topicId !== id)
        : [...current, id]
    )
  }
  const isTopicGroupExpanded = (id: string) => expandedTopicIds.includes(id)
  const selectedTopicCount =
    selectedTopics === null ? topicOptions.length : selectedTopics.length

  const normalizedTopicSearch = topicSearch.trim().toLowerCase()
  const visibleTopicOptions = normalizedTopicSearch
    ? topicOptions.filter((topic) => {
        const matches = (value: string) =>
          (value || '').toLowerCase().includes(normalizedTopicSearch)
        const childMatch = (topic.children ?? []).some((child) =>
          matches(getTopicName(child))
        )
        return matches(getTopicName(topic)) || childMatch
      })
    : topicOptions
  const allOperatorsSelected = selectedOperators === null
  const isOperatorSelected = (id: string) =>
    selectedOperators === null || selectedOperators.includes(id)
  const toggleOperator = (id: string) => {
    setSelectedOperators((current) => {
      const currentSelection =
        current === null
          ? operatorOptions.map((operator) => operator.id)
          : current
      const nextSelection = currentSelection.includes(id)
        ? currentSelection.filter((operatorId) => operatorId !== id)
        : [...currentSelection, id]

      if (
        nextSelection.length === operatorOptions.length &&
        operatorOptions.length > 0
      ) {
        return null
      }

      return nextSelection
    })
  }
  return (
    <div className={classes.inboxPreview}>
      <div className={classes.header}>
        <h2 className={classes.title}>Inbox Preview</h2>
        <div className={classes.platformTabs}>
          {platformOptions.map((platform) => (
            <button
              className={`${classes.platformTab} ${
                activePlatform === platform.id ? classes.activePlatform : ''
              }`}
              key={platform.id}
              onClick={() =>
                setSelectedPlatforms(platform.id === 'all' ? [] : [platform.id])
              }
              type="button"
            >
              {platform.label}
            </button>
          ))}
        </div>
      </div>

      {showEmptyState ? (
        <div className={classes.emptyStatePanel}>
          <div className={classes.emptyStateContent}>
            <span className={classes.emptyStateIconBox}>
              {emptyStateVariant === 'no_date_range_data' ? (
                <NoDateRangeDataIcon className={classes.emptyStateIcon} />
              ) : (
                <EmptyInboxIcon className={classes.emptyStateIcon} />
              )}
            </span>
            {emptyStateVariant === 'no_date_range_data' ? (
              <>
                <h3>No data for this period</h3>
                <p>
                  There is no available data within the selected date range.
                </p>
              </>
            ) : (
              <>
                <h3>No data available yet</h3>
                <p>
                  This platform doesn&apos;t have any connected campaign data to
                  display.
                </p>
              </>
            )}
          </div>
        </div>
      ) : (
        <div
          className={`${classes.body} ${
            activeConversation ? classes.bodySplit : ''
          }`}
        >
          {/* ===== Left: tabs + list ===== */}
          <div className={classes.leftColumn}>
            <div className={classes.leftControls}>
              <div className={classes.tabsRow}>
                <div className={classes.tabs}>
                  {TABS.map((tab) => (
                    <button
                      className={`${classes.tab} ${
                        activeTab === tab.key ? classes.active : ''
                      }`}
                      key={tab.key}
                      onClick={() => setActiveTab(tab.key)}
                      type="button"
                    >
                      {tab.label}
                    </button>
                  ))}
                </div>
              </div>

              {conversionDisplay === 'prominent' && (
                <ConversionOutcomeBar
                  counts={conversionCounts}
                  onChange={setSelectedConversion}
                  value={selectedConversion}
                />
              )}

              <div className={classes.filtersRow}>
                <div
                  className={`${classes.operatorDropdown} ${
                    openFilter === 'operator' ? classes.dropdownOpen : ''
                  }`}
                  ref={operatorDropdownRef}
                >
                  <button
                    className={`${classes.operatorDropdownTrigger} ${
                      openFilter === 'operator' ? classes.open : ''
                    }`}
                    onClick={() =>
                      setOpenFilter(
                        openFilter === 'operator' ? null : 'operator'
                      )
                    }
                    title={`Operator: ${operatorValue}`}
                    type="button"
                  >
                    <span className={classes.operatorDropdownLabel}>
                      Operator
                    </span>
                    <span className={classes.operatorDropdownValue}>
                      <span className={classes.filterValueText}>
                        {operatorValue.startsWith('All ') ? (
                          <>
                            <span className={classes.filterValueAccent}>
                              All
                            </span>{' '}
                            {operatorValue.replace(/^All\s+/, '')}
                          </>
                        ) : (
                          operatorValue
                        )}
                      </span>
                      <ChevronIcon />
                    </span>
                  </button>

                  {openFilter === 'operator' && (
                    <div className={classes.operatorDropdownPanel}>
                      <div className={classes.operatorDropdownSearch}>
                        <svg aria-hidden="true" fill="none" viewBox="0 0 16 16">
                          <circle
                            cx="7"
                            cy="7"
                            r="5"
                            stroke="currentColor"
                            strokeWidth="1.5"
                          />
                          <path
                            d="M10.75 10.75L14 14"
                            stroke="currentColor"
                            strokeLinecap="round"
                            strokeWidth="1.5"
                          />
                        </svg>
                        <input
                          autoFocus
                          className={classes.operatorDropdownSearchInput}
                          onChange={(event) =>
                            setOperatorSearch(event.target.value)
                          }
                          placeholder="Operator"
                          type="text"
                          value={operatorSearch}
                        />
                        <button
                          className={classes.operatorDropdownSearchValue}
                          onClick={() => setOpenFilter(null)}
                          type="button"
                        >
                          <span className={classes.filterValueAccent}>All</span>
                          Operators Selected
                          <ChevronIcon />
                        </button>
                      </div>

                      <button
                        className={classes.operatorSelectAll}
                        onClick={() =>
                          setSelectedOperators(allOperatorsSelected ? [] : null)
                        }
                        type="button"
                      >
                        <span
                          className={`${classes.operatorCheckbox} ${
                            allOperatorsSelected ? classes.checked : ''
                          }`}
                        >
                          {allOperatorsSelected && (
                            <svg
                              aria-hidden="true"
                              fill="none"
                              viewBox="0 0 16 16"
                            >
                              <path
                                d="M3.5 8.2L6.5 11L12.5 5"
                                stroke="currentColor"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="1.8"
                              />
                            </svg>
                          )}
                        </span>
                        Select All Operators
                      </button>

                      <div className={classes.operatorPanelSectionTitle}>
                        Operators
                      </div>

                      <div className={classes.operatorPanelList}>
                        {visibleOperatorOptions.length === 0 && (
                          <div className={classes.operatorPanelEmpty}>
                            No operators found
                          </div>
                        )}
                        {visibleOperatorOptions.map((operator) => (
                          <button
                            className={classes.operatorPanelOption}
                            key={operator.id}
                            onClick={() => toggleOperator(operator.id)}
                            type="button"
                          >
                            <span
                              className={`${classes.operatorCheckbox} ${
                                isOperatorSelected(operator.id)
                                  ? classes.checked
                                  : ''
                              }`}
                            >
                              {isOperatorSelected(operator.id) && (
                                <svg
                                  aria-hidden="true"
                                  fill="none"
                                  viewBox="0 0 16 16"
                                >
                                  <path
                                    d="M3.5 8.2L6.5 11L12.5 5"
                                    stroke="currentColor"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="1.8"
                                  />
                                </svg>
                              )}
                            </span>
                            <span className={classes.operatorPanelName}>
                              {operator.name}
                            </span>
                            <span className={classes.operatorPanelCount}>
                              · {operator.count.toLocaleString()} Chats
                            </span>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                <span className={classes.controlsDivider} />
                <PendingDataFilter label="Audience" value="New / Returning" />
                <span className={classes.controlsDivider} />
                {summary?.sourceAvailable ? (
                  <SourceFilter
                    onChange={setSelectedSource}
                    value={selectedSource}
                  />
                ) : (
                  <PendingDataFilter label="Source" value="Organic / Paid" />
                )}
                {conversionDisplay === 'subtle' && (
                  <>
                    <span className={classes.controlsDivider} />
                    <SingleChoiceFilter<Exclude<ConversionFilterValue, 'all'>>
                      allValueLabel="Statuses"
                      label="Conversion"
                      onChange={setSelectedConversion}
                      options={CONVERSION_FILTER_OPTIONS.map((option) => ({
                        ...option,
                        count:
                          option.id === 'all'
                            ? undefined
                            : conversionCounts?.[option.id],
                      }))}
                      value={selectedConversion}
                    />
                  </>
                )}
              </div>
            </div>

            {checkedIds.size > 0 && (
              <div className={classes.selectionBar}>
                <div className={classes.selectionIcons}>
                  <div
                    className={classes.selectionAction}
                    onClick={toggleCheckAll}
                  >
                    {allChecked ? (
                      <CheckedIcon className={classes.selectionIcon} />
                    ) : (
                      <CheckboxWhiteIcon className={classes.selectionIcon} />
                    )}
                    <div
                      className={`${classes.selectionHint} ${classes.selectionHintStart}`}
                    >
                      {en.selectAll}
                    </div>
                  </div>

                  <div
                    className={classes.selectionAction}
                    onClick={clearSelection}
                  >
                    <DeselectIcon className={classes.selectionIcon} />
                    <div className={classes.selectionHint}>
                      {en.deselectAll}
                    </div>
                  </div>

                  <span className={classes.selectionDivider} />

                  <div
                    className={`${classes.selectionAction} ${
                      sentimentMenuOpen ? classes.selectionActionOpen : ''
                    }`}
                    ref={sentimentMenuRef}
                  >
                    <SentimentChangeIcon
                      onClick={() => setSentimentMenuOpen((open) => !open)}
                    />
                    {!sentimentMenuOpen && (
                      <div className={classes.selectionHint}>
                        {en.changeSentiment}
                      </div>
                    )}
                    {sentimentMenuOpen && (
                      <div className={classes.sentimentActionMenu}>
                        {SENTIMENT_ACTIONS.map((action) => (
                          <button
                            className={classes.sentimentActionOption}
                            disabled={isChangingSentiment}
                            key={action.key}
                            onClick={() => handleSentimentAction(action.key)}
                            type="button"
                          >
                            <span
                              className={`${classes.sentimentActionDot} ${
                                classes[action.key]
                              }`}
                            />
                            <span>
                              Mark as: <strong>{action.label}</strong>
                            </span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  {conversionOn && (
                    <div
                      className={classes.selectionAction}
                      ref={conversionMenuRef}
                    >
                      <button
                        aria-expanded={conversionMenuOpen}
                        aria-haspopup="menu"
                        aria-label={en.changeConversionStatus}
                        className={classes.selectionIconButton}
                        onClick={() => setConversionMenuOpen((open) => !open)}
                        type="button"
                      >
                        <MarkConversionGlyph
                          className={`${conversionClasses.markGlyph} ${
                            conversionMenuOpen ? conversionClasses.open : ''
                          }`}
                        />
                      </button>
                      {!conversionMenuOpen && (
                        <div className={classes.selectionHint}>
                          {en.changeConversionStatus}
                        </div>
                      )}
                      {conversionMenuOpen && (
                        <div
                          className={classes.sentimentActionMenu}
                          role="menu"
                        >
                          {CONVERSION_ACTIONS.map((action) => (
                            <button
                              className={classes.sentimentActionOption}
                              disabled={isChangingConversion}
                              key={action.key}
                              onClick={() => handleConversionAction(action.key)}
                              role="menuitem"
                              type="button"
                            >
                              <span
                                className={`${conversionClasses.actionIcon} ${
                                  conversionClasses[action.key]
                                }`}
                              >
                                <ConversionActionIcon action={action.key} />
                              </span>
                              <span>
                                Mark as: <strong>{action.label}</strong>
                              </span>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <span className={classes.selectedCount}>
                  Selected chat:{' '}
                  <strong className={classes.selectedCountNumber}>
                    ({checkedIds.size})
                  </strong>
                </span>
              </div>
            )}

            {isUpdating && <div className={classes.updatingBar} />}

            <div
              className={`${classes.list} ${isUpdating ? classes.updating : ''}`}
              onScroll={handleScroll}
            >
              {conversationsLoading &&
                Array.from({ length: 6 }).map((_, i) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <ConversationRowSkeleton key={`skeleton-${i}`} />
                ))}

              {!conversationsLoading && conversations.length === 0 && (
                <div className={classes.listEmpty}>
                  <span className={classes.listEmptyIconBox}>
                    <EmptyInboxIcon className={classes.listEmptyIcon} />
                  </span>
                  <h4 className={classes.listEmptyTitle}>
                    {listEmptyCopy.title}
                  </h4>
                  <p className={classes.listEmptySubtitle}>
                    {listEmptyCopy.subtitle}
                  </p>
                </div>
              )}

              {conversations.map((msg) => {
                const operatorName = msg.operator?.name || 'Unassigned'
                const isActive = activeConversation?.id === msg.id
                const isChecked = checkedIds.has(msg.id)
                return (
                  <div
                    className={`${classes.row} ${
                      isActive ? classes.rowActive : ''
                    } ${isChecked ? classes.rowChecked : ''}`}
                    key={msg.id}
                    onClick={() => setActiveConversation(msg)}
                    // The target check keeps Enter/Space on the checkbox or the
                    // external link from also opening the chat.
                    onKeyDown={(event) => {
                      if (
                        event.target === event.currentTarget &&
                        (event.key === 'Enter' || event.key === ' ')
                      ) {
                        event.preventDefault()
                        setActiveConversation(msg)
                      }
                    }}
                    role="button"
                    tabIndex={0}
                  >
                    {/* Only the checkbox selects; revealed on row hover or
                        when ticked (CSS). The click stays off the row so
                        ticking a chat never opens it. */}
                    <span className={classes.rowCheckboxWrap}>
                      <input
                        aria-label={isChecked ? 'Unselect chat' : 'Select chat'}
                        checked={isChecked}
                        className={classes.rowCheckbox}
                        onChange={() => toggleChecked(msg.id)}
                        onClick={(event) => event.stopPropagation()}
                        type="checkbox"
                      />
                    </span>
                    <div className={classes.avatar}>
                      {avatarContent(msg.platform, msg.user.name)}
                      {msg.conversion &&
                        isConversionDisplayOn(conversionDisplay) && (
                          <ConversionAvatarMarker status={msg.conversion} />
                        )}
                    </div>

                    <div className={classes.message}>
                      <div className={classes.messageHeader}>
                        {platformIcon(msg.platform)}
                        <TruncatedTooltipText
                          className={classes.userName}
                          text={msg.user.name}
                          tooltipClassName={classes.operatorTooltip}
                          wrapClassName={classes.userNameWrap}
                        >
                          {msg.user.name}
                        </TruncatedTooltipText>
                        <span className={classes.timeAgo}>
                          · {formatRelative(msg.lastActivityAt)}
                        </span>
                        {(() => {
                          const href = resolveExternalUrl(msg.externalUrl)
                          if (!href) return null
                          return (
                            <a
                              className={classes.linkIcon}
                              href={href}
                              onClick={(e) => e.stopPropagation()}
                              rel="noopener noreferrer"
                              target="_blank"
                            >
                              <LinkIcon />
                            </a>
                          )
                        })()}
                        {/* New / Returning and Organic / Paid tags — sent once
                            the collector records them (HAP-502). */}
                        {msg.audience && (
                          <span
                            className={`${classes.chatTag} ${
                              classes[msg.audience]
                            }`}
                          >
                            {msg.audience === 'new' ? 'New' : 'Returning'}
                          </span>
                        )}
                        {(msg.origin === 'organic' ||
                          msg.origin === 'paid') && (
                          <span
                            className={`${classes.chatTag} ${
                              classes[msg.origin]
                            }`}
                          >
                            {msg.origin === 'organic' ? 'Organic' : 'Paid'}
                          </span>
                        )}
                      </div>
                      {debouncedSearch && msg.match ? (
                        <MatchCell
                          highlight={debouncedSearch}
                          match={msg.match}
                        />
                      ) : (
                        <SnippetCell
                          highlight={debouncedSearch || undefined}
                          text={msg.snippet}
                        />
                      )}
                    </div>

                    <span
                      className={`${classes.sentimentPill} ${
                        classes[msg.sentiment]
                      }`}
                    >
                      {msg.sentiment}
                    </span>

                    <span className={classes.operator}>
                      <PersonIcon className={classes.operatorRowIcon} />
                      <span className={classes.operatorLabel}>Operator: </span>
                      <TruncatedTooltipText
                        className={classes.operatorName}
                        text={operatorName}
                        tooltipClassName={classes.operatorTooltip}
                        wrapClassName={classes.operatorNameWrap}
                      >
                        {operatorName}
                      </TruncatedTooltipText>
                    </span>
                  </div>
                )
              })}

              {isFetchingNextPage &&
                Array.from({ length: 2 }).map((_, i) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <ConversationRowSkeleton key={`skeleton-more-${i}`} />
                ))}
            </div>
          </div>

          {/* ===== Right: donut + filters OR conversation panel ===== */}
          {activeConversation ? (
            <ConversationPanel
              conversation={activeConversation}
              conversionDisplay={conversionDisplay}
              highlight={debouncedSearch || undefined}
              isChangingConversion={
                isChangingConversion &&
                !!conversionVariables?.conversationIds.includes(
                  activeConversation.id
                )
              }
              isLoading={messagesLoading}
              messages={messagesData?.messages ?? []}
              onClose={() => setActiveConversation(null)}
              onConversionChange={(action) =>
                applyConversionStatus([activeConversation.id], action)
              }
            />
          ) : (
            <div className={classes.sidebar}>
              <div className={classes.donutCard}>
                <Donut
                  negative={donut?.negative.count ?? 0}
                  neutral={donut?.neutral.count ?? 0}
                  percent={donut?.positive.percentage ?? 0}
                  positive={donut?.positive.count ?? 0}
                />
                <div className={classes.donutInfo}>
                  <span className={classes.donutHeading}>
                    {summaryLoading
                      ? '…'
                      : dominantHeading(donut?.dominantSentiment)}
                  </span>
                  <span className={classes.donutSub}>
                    Out of{' '}
                    <strong>
                      {(donut?.total ?? 0).toLocaleString()} Chats
                    </strong>
                  </span>
                  <div className={classes.donutLegend}>
                    <span
                      className={`${classes.donutLegendItem} ${classes.positive}`}
                    >
                      Positive
                    </span>
                    <span
                      className={`${classes.donutLegendItem} ${classes.neutral}`}
                    >
                      Neutral
                    </span>
                    <span
                      className={`${classes.donutLegendItem} ${classes.negative}`}
                    >
                      Negative
                    </span>
                  </div>
                </div>
              </div>

              <AudienceSourceSummary sourceCounts={summary?.sourceCounts} />

              <div className={classes.filters}>
                <div className={classes.topicDropdownPanel}>
                  <div className={classes.topicSearchRow}>
                    <svg aria-hidden="true" fill="none" viewBox="0 0 16 16">
                      <circle
                        cx="7"
                        cy="7"
                        r="5"
                        stroke="currentColor"
                        strokeWidth="1.5"
                      />
                      <path
                        d="M10.75 10.75L14 14"
                        stroke="currentColor"
                        strokeLinecap="round"
                        strokeWidth="1.5"
                      />
                    </svg>
                    <input
                      autoFocus
                      className={classes.topicSearchInput}
                      onChange={(event) => setTopicSearch(event.target.value)}
                      placeholder="Search for Topic or Messages"
                      type="text"
                      value={topicSearch}
                    />
                    <button
                      className={`${classes.topicSearchValue} ${
                        topicListOpen ? '' : classes.collapsed
                      }`}
                      onClick={() => setTopicListOpen(!topicListOpen)}
                      type="button"
                    >
                      <span className={classes.filterValueAccent}>
                        {selectedTopicCount}
                      </span>
                      {selectedTopicCount === 1
                        ? 'Topic Selected'
                        : 'Topics Selected'}
                      <ChevronIcon />
                    </button>
                  </div>

                  {topicListOpen && (
                    <>
                      <button
                        className={classes.topicSelectAll}
                        onClick={toggleAllTopics}
                        type="button"
                      >
                        <span
                          className={`${classes.operatorCheckbox} ${
                            allTopicsSelected ? classes.checked : ''
                          }`}
                        >
                          {allTopicsSelected && (
                            <svg
                              aria-hidden="true"
                              fill="none"
                              viewBox="0 0 16 16"
                            >
                              <path
                                d="M3.5 8.2L6.5 11L12.5 5"
                                stroke="currentColor"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="1.8"
                              />
                            </svg>
                          )}
                        </span>
                        Select all topics
                      </button>

                      <div className={classes.topicPanelList}>
                        {visibleTopicOptions.length === 0 && (
                          <div className={classes.topicPanelEmpty}>
                            No topics found
                          </div>
                        )}
                        {visibleTopicOptions.map((topic, index) => {
                          const children = Array.isArray(topic.children)
                            ? topic.children
                            : []
                          const topicId = getTopicId(topic) || `topic-${index}`
                          const isExpanded = isTopicGroupExpanded(topicId)

                          return (
                            <div
                              className={`${classes.topicGroup} ${
                                isExpanded ? classes.expanded : ''
                              }`}
                              key={topicId}
                            >
                              <div className={classes.topicParentRow}>
                                <button
                                  className={`${classes.topicChevronBox} ${
                                    isExpanded ? classes.expanded : ''
                                  }`}
                                  disabled={!children.length}
                                  onClick={() => {
                                    if (children.length)
                                      toggleTopicGroup(topicId)
                                  }}
                                  type="button"
                                >
                                  <ChevronIcon />
                                </button>
                                <button
                                  className={`${classes.operatorCheckbox} ${
                                    isTopicSelected(topicId)
                                      ? classes.checked
                                      : ''
                                  }`}
                                  onClick={() => toggleTopic(topicId)}
                                  type="button"
                                >
                                  {isTopicSelected(topicId) && (
                                    <svg
                                      aria-hidden="true"
                                      fill="none"
                                      viewBox="0 0 16 16"
                                    >
                                      <path
                                        d="M3.5 8.2L6.5 11L12.5 5"
                                        stroke="currentColor"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth="1.8"
                                      />
                                    </svg>
                                  )}
                                </button>
                                <TruncatedTooltipText
                                  className={classes.topicName}
                                  text={getTopicName(topic)}
                                  tooltipClassName={classes.operatorTooltip}
                                  wrapClassName={classes.topicNameWrap}
                                >
                                  {getTopicName(topic)}
                                </TruncatedTooltipText>
                                <span className={classes.topicCount}>
                                  · Chats -{' '}
                                  <strong>
                                    {formatTopicCount(getTopicCount(topic))}
                                  </strong>
                                </span>
                              </div>

                              {isExpanded &&
                                children.map((child, childIndex) => {
                                  const childId =
                                    getTopicId(child) ||
                                    `${topicId}-child-${childIndex}`

                                  return (
                                    <button
                                      className={classes.topicChildRow}
                                      key={childId}
                                      onClick={() => toggleTopic(childId)}
                                      type="button"
                                    >
                                      <span
                                        className={`${classes.operatorCheckbox} ${
                                          isTopicSelected(childId)
                                            ? classes.checked
                                            : ''
                                        }`}
                                      >
                                        {isTopicSelected(childId) && (
                                          <svg
                                            aria-hidden="true"
                                            fill="none"
                                            viewBox="0 0 16 16"
                                          >
                                            <path
                                              d="M3.5 8.2L6.5 11L12.5 5"
                                              stroke="currentColor"
                                              strokeLinecap="round"
                                              strokeLinejoin="round"
                                              strokeWidth="1.8"
                                            />
                                          </svg>
                                        )}
                                      </span>
                                      <TruncatedTooltipText
                                        className={classes.topicChildName}
                                        text={getTopicName(child)}
                                        tooltipClassName={
                                          classes.operatorTooltip
                                        }
                                        wrapClassName={classes.topicNameWrap}
                                      >
                                        {getTopicName(child)}
                                      </TruncatedTooltipText>
                                      <span className={classes.topicChildCount}>
                                        Chats -{' '}
                                        {formatTopicCount(getTopicCount(child))}
                                      </span>
                                    </button>
                                  )
                                })}
                            </div>
                          )
                        })}
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
